/**
 * IntakeTab.jsx — Admin Dashboard Tab
 *
 * Shows referral pipeline summary, pending intakes, and recent admissions.
 * Used inside AdminDashBoard.jsx as a tab component.
 *
 * Powered by Cold Harbor Digital
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  onOrgReferrals, updateReferralStatus, getTeamMembers,
  REFERRAL_SOURCES, PROGRAMS,
} from "../../services/intakeService";
import {
  Plus, UserPlus, PhoneCall, Calendar, CheckCircle,
  XCircle, Clock, ChevronRight, AlertTriangle, ArrowRight,
  ClipboardList, Users, TrendingUp,
} from "lucide-react";

const STATUS_CONFIG = {
  new: { label: "New", color: "#63b3ed", bg: "rgba(99,179,237,0.1)", icon: Clock },
  contacted: { label: "Contacted", color: "#f6ad55", bg: "rgba(246,173,85,0.1)", icon: PhoneCall },
  scheduled: { label: "Scheduled", color: "#b794f4", bg: "rgba(183,148,244,0.1)", icon: Calendar },
  admitted: { label: "Admitted", color: "#68d391", bg: "rgba(104,211,145,0.1)", icon: CheckCircle },
  closed: { label: "Closed", color: "#718096", bg: "rgba(113,128,150,0.1)", icon: XCircle },
};

const URGENCY_COLORS = { routine: "#68d391", urgent: "#ecc94b", emergent: "#fc8181" };

const S = {
  card: {
    background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)",
    borderRadius: 16, padding: 16, marginBottom: 12,
  },
};

export default function IntakeTab() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const org = profile?.organization || localStorage.getItem("fieldnotes_user_org") || "default-org";

  const [referrals, setReferrals] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    const unsub = onOrgReferrals(org, setReferrals);
    getTeamMembers(org).then(setTeamMembers);
    return unsub;
  }, [org]);

  // Counts
  const counts = {};
  Object.keys(STATUS_CONFIG).forEach(s => { counts[s] = referrals.filter(r => r.status === s).length; });
  const activeReferrals = referrals.filter(r => !["admitted", "closed"].includes(r.status));
  const recentAdmissions = referrals.filter(r => r.status === "admitted").slice(0, 5);
  const urgentReferrals = activeReferrals.filter(r => r.urgency === "urgent" || r.urgency === "emergent");

  // Avg days from referral to admission (last 30 days)
  const recentAdmitted = referrals.filter(r => {
    if (r.status !== "admitted" || !r.admittedDate || !r.referralDate) return false;
    const admitted = new Date(r.admittedDate);
    return (Date.now() - admitted.getTime()) < 30 * 24 * 60 * 60 * 1000;
  });
  const avgDaysToAdmit = recentAdmitted.length > 0
    ? Math.round(recentAdmitted.reduce((sum, r) => {
        const days = (new Date(r.admittedDate) - new Date(r.referralDate)) / (1000 * 60 * 60 * 24);
        return sum + days;
      }, 0) / recentAdmitted.length)
    : null;

  const formatDate = (d) => {
    if (!d) return "—";
    if (d.toDate) return d.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const timeSince = (d) => {
    if (!d) return "";
    const date = d.toDate ? d.toDate() : new Date(d);
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "1d";
    return `${days}d`;
  };

  return (
    <div>
      {/* Pipeline Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 16 }}>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <div key={key} style={{
              ...S.card, textAlign: "center", padding: 12, marginBottom: 0,
              borderColor: counts[key] > 0 ? `${cfg.color}22` : undefined,
            }}>
              <Icon size={16} style={{ color: cfg.color, margin: "0 auto 4px" }} />
              <div style={{ fontSize: 20, fontWeight: 900, color: cfg.color, fontFamily: "monospace" }}>
                {counts[key] || 0}
              </div>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {cfg.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
        <div style={{ ...S.card, textAlign: "center", padding: 12, marginBottom: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#f6ad55", fontFamily: "monospace" }}>
            {activeReferrals.length}
          </div>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#718096", textTransform: "uppercase" }}>Active Pipeline</div>
        </div>
        <div style={{ ...S.card, textAlign: "center", padding: 12, marginBottom: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: urgentReferrals.length > 0 ? "#fc8181" : "#68d391", fontFamily: "monospace" }}>
            {urgentReferrals.length}
          </div>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#718096", textTransform: "uppercase" }}>Urgent</div>
        </div>
        <div style={{ ...S.card, textAlign: "center", padding: 12, marginBottom: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#4fd1c5", fontFamily: "monospace" }}>
            {avgDaysToAdmit !== null ? `${avgDaysToAdmit}d` : "—"}
          </div>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#718096", textTransform: "uppercase" }}>Avg to Admit</div>
        </div>
      </div>

      {/* Urgent / Emergent Alerts */}
      {urgentReferrals.length > 0 && (
        <div style={{
          ...S.card, borderColor: "rgba(252,129,129,0.15)", background: "rgba(252,129,129,0.04)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
            <AlertTriangle size={14} style={{ color: "#fc8181" }} />
            <span style={{ fontSize: 12, fontWeight: 800, color: "#fc8181" }}>
              {urgentReferrals.length} Urgent/Emergent Referral{urgentReferrals.length > 1 ? "s" : ""}
            </span>
          </div>
          {urgentReferrals.slice(0, 3).map(ref => (
            <div key={ref.id} onClick={() => navigate("/referrals")} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 0", borderTop: "1px solid rgba(252,129,129,0.08)", cursor: "pointer",
            }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>
                  {ref.firstName} {ref.lastName}
                </span>
                <span style={{
                  marginLeft: 8, fontSize: 9, fontWeight: 800, textTransform: "uppercase",
                  color: URGENCY_COLORS[ref.urgency], padding: "1px 6px", borderRadius: 10,
                  background: `${URGENCY_COLORS[ref.urgency]}15`,
                }}>
                  {ref.urgency}
                </span>
              </div>
              <span style={{ fontSize: 10, color: "#718096" }}>{timeSince(ref.createdAt)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Active Pipeline */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ClipboardList size={14} style={{ color: "#f6ad55" }} />
            <span style={{ fontSize: 13, fontWeight: 800, color: "#f6ad55" }}>Active Referrals</span>
          </div>
          <button onClick={() => navigate("/referrals")} style={{
            background: "none", border: "none", cursor: "pointer", color: "#63b3ed",
            fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 4,
          }}>
            View All <ChevronRight size={12} />
          </button>
        </div>

        {activeReferrals.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 12, color: "#4a5568" }}>No active referrals</div>
          </div>
        ) : (
          activeReferrals.slice(0, 8).map(ref => {
            const statusCfg = STATUS_CONFIG[ref.status];
            const StatusIcon = statusCfg.icon;
            return (
              <div key={ref.id} onClick={() => navigate("/referrals")} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 0", borderTop: "1px solid rgba(99,179,237,0.04)", cursor: "pointer",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                    background: statusCfg.bg,
                  }}>
                    <StatusIcon size={12} style={{ color: statusCfg.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>
                      {ref.firstName} {ref.lastName}
                    </div>
                    <div style={{ fontSize: 10, color: "#718096" }}>
                      {REFERRAL_SOURCES.find(s => s.value === ref.sourceType)?.label || "—"}
                      {ref.assignedToName && ` · ${ref.assignedToName}`}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {ref.urgency !== "routine" && (
                    <span style={{
                      fontSize: 8, fontWeight: 800, textTransform: "uppercase",
                      color: URGENCY_COLORS[ref.urgency],
                    }}>
                      {ref.urgency}
                    </span>
                  )}
                  <span style={{
                    fontSize: 9, fontWeight: 700, color: statusCfg.color,
                    padding: "2px 8px", borderRadius: 10, background: statusCfg.bg,
                  }}>
                    {statusCfg.label}
                  </span>
                  <span style={{ fontSize: 10, color: "#4a5568" }}>{timeSince(ref.createdAt)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Recent Admissions */}
      {recentAdmissions.length > 0 && (
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
            <CheckCircle size={14} style={{ color: "#68d391" }} />
            <span style={{ fontSize: 13, fontWeight: 800, color: "#68d391" }}>Recent Admissions</span>
          </div>
          {recentAdmissions.map(ref => (
            <div key={ref.id} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 0", borderTop: "1px solid rgba(99,179,237,0.04)",
            }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>
                  {ref.firstName} {ref.lastName}
                </span>
                <span style={{ fontSize: 10, color: "#718096", marginLeft: 8 }}>
                  {PROGRAMS.find(p => p.value === ref.requestedProgram)?.label || ref.requestedProgram}
                </span>
              </div>
              <span style={{ fontSize: 10, color: "#68d391" }}>{formatDate(ref.admittedDate)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <button onClick={() => navigate("/referrals")} style={{
          ...S.card, marginBottom: 0, cursor: "pointer", textAlign: "center",
          border: "1px solid rgba(246,173,85,0.15)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: 16,
        }}>
          <ClipboardList size={20} style={{ color: "#f6ad55" }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: "#f6ad55" }}>Manage Referrals</span>
        </button>
        <button onClick={() => navigate("/intake")} style={{
          ...S.card, marginBottom: 0, cursor: "pointer", textAlign: "center",
          border: "1px solid rgba(104,211,145,0.15)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: 16,
        }}>
          <UserPlus size={20} style={{ color: "#68d391" }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: "#68d391" }}>New Intake</span>
        </button>
      </div>
    </div>
  );
}